import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import PageHeader from "../components/PageHeader";

type AccountRole = "admin" | "user";

type UserRow = {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  accountRole: AccountRole;
  createdAtLabel: string;
  updatedAtLabel: string;
  lastLoginAtLabel: string;
  ownedSurveyCount: number;
  ownedListCount: number;
  editorSurveyCount: number;
  viewerSurveyCount: number;
};

function formatTimestamp(value: any): string {
  const date = value?.toDate?.();
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

export default function AdminUsers() {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setErrMsg("You need to sign in to view users.");
        setLoading(false);
        return;
      }

      try {
        const meSnap = await getDoc(doc(db, "users", currentUser.uid));
        const myRole = meSnap.exists() ? (meSnap.data() as any)?.accountRole : undefined;
        const admin = myRole === "admin";
        setIsAdmin(admin);

        if (!admin) {
          setErrMsg("Admin access is required.");
          setLoading(false);
          return;
        }

        const [userSnap, surveySnap, listSnap] = await Promise.all([
          getDocs(query(collection(db, "users"), orderBy("emailLower"))),
          getDocs(collection(db, "surveys")),
          getDocs(collection(db, "lists")),
        ]);

        const surveyCounts = new Map<string, number>();
        const editorCounts = new Map<string, number>();
        const viewerCounts = new Map<string, number>();
        surveySnap.docs.forEach((surveyDoc) => {
          const data = surveyDoc.data() as any;
          const ownerUid = data.ownerId ?? data.userId;
          if (!ownerUid) return;
          surveyCounts.set(ownerUid, (surveyCounts.get(ownerUid) ?? 0) + 1);

          const roles = data.roles ?? {};
          Object.entries(roles).forEach(([uid, role]) => {
            if (role === "editor") {
              editorCounts.set(uid, (editorCounts.get(uid) ?? 0) + 1);
            } else if (role === "viewer") {
              viewerCounts.set(uid, (viewerCounts.get(uid) ?? 0) + 1);
            }
          });
        });

        const listCounts = new Map<string, number>();
        listSnap.docs.forEach((listDoc) => {
          const data = listDoc.data() as any;
          const ownerUid = data.ownerId ?? data.userId;
          if (!ownerUid) return;
          listCounts.set(ownerUid, (listCounts.get(ownerUid) ?? 0) + 1);
        });

        const nextRows: UserRow[] = userSnap.docs.map((userDoc) => {
          const data = userDoc.data() as any;
          return {
            id: userDoc.id,
            uid: userDoc.id,
            email: data.email ?? "",
            displayName: data.displayName ?? "",
            firstName: data.firstName ?? "",
            lastName: data.lastName ?? "",
            accountRole: (data.accountRole === "admin" ? "admin" : "user") as AccountRole,
            createdAtLabel: formatTimestamp(data.createdAt),
            updatedAtLabel: formatTimestamp(data.updatedAt),
            lastLoginAtLabel: formatTimestamp(data.lastLoginAt),
            ownedSurveyCount: surveyCounts.get(userDoc.id) ?? 0,
            ownedListCount: listCounts.get(userDoc.id) ?? 0,
            editorSurveyCount: editorCounts.get(userDoc.id) ?? 0,
            viewerSurveyCount: viewerCounts.get(userDoc.id) ?? 0,
          };
        });
        setRows(nextRows);
      } catch (err: any) {
        setErrMsg(err?.message || "Failed to load users.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const handleRoleChange = async (uid: string, nextRole: AccountRole) => {
    const previous = rows.find((row) => row.uid === uid)?.accountRole ?? "user";
    setRows((current) =>
      current.map((row) => (row.uid === uid ? { ...row, accountRole: nextRole } : row))
    );

    try {
      await setDoc(
        doc(db, "users", uid),
        {
          accountRole: nextRole,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err: any) {
      setRows((current) =>
        current.map((row) => (row.uid === uid ? { ...row, accountRole: previous } : row))
      );
      setErrMsg(err?.message || "Failed to update account role.");
    }
  };

  const columns = useMemo<GridColDef<UserRow>[]>(
    () => [
      { field: "email", headerName: "Email", flex: 1.4, minWidth: 220 },
      { field: "displayName", headerName: "Display Name", flex: 1, minWidth: 170 },
      { field: "firstName", headerName: "First Name", flex: 0.8, minWidth: 140 },
      { field: "lastName", headerName: "Last Name", flex: 0.8, minWidth: 140 },
      {
        field: "ownedSurveyCount",
        headerName: "Owned Surveys",
        type: "number",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "ownedListCount",
        headerName: "Owned Lists",
        type: "number",
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "editorSurveyCount",
        headerName: "Editor On",
        type: "number",
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "viewerSurveyCount",
        headerName: "Viewer On",
        type: "number",
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "accountRole",
        headerName: "Role",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <FormControl size="small" fullWidth>
            <InputLabel id={`role-${params.row.uid}`}>Role</InputLabel>
            <Select
              labelId={`role-${params.row.uid}`}
              value={params.row.accountRole}
              label="Role"
              onChange={(event: SelectChangeEvent<AccountRole>) =>
                handleRoleChange(params.row.uid, event.target.value as AccountRole)
              }
            >
              <MenuItem value="user">user</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </Select>
          </FormControl>
        ),
      },
      { field: "lastLoginAtLabel", headerName: "Last Login", flex: 1, minWidth: 180 },
      { field: "createdAtLabel", headerName: "Created", flex: 1, minWidth: 180 },
      { field: "updatedAtLabel", headerName: "Updated", flex: 1, minWidth: 180 },
      { field: "uid", headerName: "UID", flex: 1.2, minWidth: 220 },
    ],
    []
  );

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading users…</Typography>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errMsg || "Admin access is required."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="User Manager"
        subtitle="Admin-only view of application users and profile metadata"
      />

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uid}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pagination
          autoHeight
          disableRowSelectionOnClick
          sx={{ "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" } }}
        />
      </Box>
    </Box>
  );
}
